'use client';

import React, { useRef, useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import CategoryCard from './CategoryCard';
import { Skeleton } from './ui/skeleton';
import { useIsTVDevice, useTVRow } from '../hooks/useTVNavigation';
import { getApiBaseUrl } from '../lib/api';

interface Category {
  id: string;
  name: string;
  displayName: string;
  imageUrl?: string | null;
}

interface CategoryRowProps {
  title: string;
  apiEndpoint: string;
  type: 'language' | 'genre';
  rowIndex?: number;
  previousRowId?: string;
  nextRowId?: string;
  upTarget?: string;
}

export default function CategoryRow({
  title,
  apiEndpoint,
  type,
  rowIndex = 0,
  previousRowId,
  nextRowId,
  upTarget = 'hero-play-button'
}: CategoryRowProps) {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const rowContainerRef = useRef<HTMLDivElement>(null);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [isVisible, setIsVisible] = useState(false);
  const isTV = useIsTVDevice();
  const categoryRefs = useRef<(HTMLDivElement | null)[]>([]);

  // Generate row ID from title
  const rowId = title.toLowerCase().replace(/\s+/g, '-');

  // Register row with navigation system
  const { registerCard, unregisterCard } = useTVRow(
    rowId,
    rowIndex,
    categories.length,
    previousRowId,
    nextRowId,
    upTarget
  );

  // Observe when row enters viewport
  useEffect(() => {
    const el = rowContainerRef.current;
    if (!el) return;

    const viewportObserver = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          viewportObserver.disconnect();
        }
      },
      { rootMargin: '200px 0px' }
    );

    viewportObserver.observe(el);
    return () => viewportObserver.disconnect();
  }, []);

  // Fetch categories from API only when visible
  useEffect(() => {
    if (!isVisible) return;

    const fetchCategories = async () => {
      try {
        setLoading(true);
        const baseUrl = getApiBaseUrl();
        const response = await fetch(`${baseUrl}${apiEndpoint}`);
        const data = await response.json();

        if (data.success && data.categories) {
          setCategories(data.categories);
          categoryRefs.current = new Array(data.categories.length).fill(null);
        }
      } catch (error) {
        console.error(`Error loading categories from ${apiEndpoint}:`, error);
      } finally {
        setLoading(false);
      }
    };

    fetchCategories();
  }, [isVisible, apiEndpoint]);

  // Register/unregister cards for TV navigation
  useEffect(() => {
    categories.forEach((_, index) => {
      if (categoryRefs.current[index]) {
        registerCard(index, categoryRefs.current[index]!);
      }
    });

    return () => {
      categories.forEach((_, index) => {
        unregisterCard(index);
      });
    };
  }, [categories, registerCard, unregisterCard]);

  // Handle scroll buttons
  const scroll = (direction: 'left' | 'right') => {
    if (scrollContainerRef.current) {
      const scrollAmount = scrollContainerRef.current.clientWidth * 0.8;
      scrollContainerRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth',
      });
    }
  };

  // Handle focus for TV navigation
  const handleCardFocus = useCallback((index: number) => {
    setFocusedIndex(index);
    
    // Scroll into view
    if (categoryRefs.current[index] && scrollContainerRef.current) {
      const card = categoryRefs.current[index]!;
      const container = scrollContainerRef.current;
      const cardLeft = card.offsetLeft;
      const cardWidth = card.offsetWidth;
      const containerWidth = container.clientWidth;
      const containerScrollLeft = container.scrollLeft;

      // Calculate if card is fully visible
      const cardRight = cardLeft + cardWidth;
      const visibleLeft = containerScrollLeft;
      const visibleRight = containerScrollLeft + containerWidth;

      // Scroll if needed
      if (cardLeft < visibleLeft) {
        container.scrollTo({
          left: cardLeft - 20,
          behavior: 'smooth',
        });
      } else if (cardRight > visibleRight) {
        container.scrollTo({
          left: cardLeft - containerWidth + cardWidth + 20,
          behavior: 'smooth',
        });
      }
    }
  }, []);

  if (!isVisible) {
    return (
      <div ref={rowContainerRef} className="mb-10 lg:mb-16 h-[260px] lg:h-[300px] px-4 lg:px-8" />
    );
  }

  if (loading) {
    return (
      <div ref={rowContainerRef} className="mb-12 px-4 lg:px-8">
        <Skeleton className="h-8 w-48 mb-6" />
        <div className="flex gap-4 overflow-hidden">
          {[...Array(6)].map((_, i) => (
            <Skeleton key={i} className="h-48 w-32 flex-shrink-0" />
          ))}
        </div>
      </div>
    );
  }

  if (categories.length === 0) {
    return null;
  }

  return (
    <div ref={rowContainerRef} className="mb-10 lg:mb-16 relative movie-row-container px-4 lg:px-8">
      {/* Row Title */}
      <h2 className="text-xl lg:text-2xl font-bold mb-4 lg:mb-6 text-white">
        {title}
      </h2>

      {/* Scroll buttons */}
      <button
        onClick={() => scroll('left')}
        className="absolute left-4 lg:left-8 top-[45%] -translate-y-1/2 z-10 bg-black/70 hover:bg-black/90 p-2 lg:p-3 rounded-r-lg opacity-0 hover:opacity-100 transition-all duration-300 hidden sm:block backdrop-blur-sm"
        aria-label="Scroll left"
        onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
      >
        <ChevronLeft className="w-6 h-6 lg:w-8 lg:h-8" />
      </button>

      <button
        onClick={() => scroll('right')}
        className="absolute right-4 lg:right-8 top-[45%] -translate-y-1/2 z-10 bg-black/70 hover:bg-black/90 p-2 lg:p-3 rounded-l-lg opacity-0 hover:opacity-100 transition-all duration-300 hidden sm:block backdrop-blur-sm"
        aria-label="Scroll right"
        onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
      >
        <ChevronRight className="w-6 h-6 lg:w-8 lg:h-8" />
      </button>

      {/* Categories Container */}
      <div
        ref={scrollContainerRef}
        className={`scroll-container ${isTV ? '' : 'gap-4 lg:gap-5 pb-2'}`}
      >
        {categories.map((category, index) => (
          <div
            key={category.id}
            className={`flex-shrink-0 ${isTV ? 'w-[180px] lg:w-[180px]' : 'w-[145px] sm:w-[165px] md:w-[185px] lg:w-[210px]'}`}
          >
            <CategoryCard
              ref={(el) => {
                categoryRefs.current[index] = el;
              }}
              id={category.id}
              name={category.name}
              displayName={category.displayName}
              type={type}
              imageUrl={category.imageUrl}
              index={index}
              isFocused={focusedIndex === index && isTV}
              isTV={isTV}
              onFocus={() => handleCardFocus(index)}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

